// The agent's PLAN / to-do list rendered inline in the chat (BUILD-DEC-099) — the ChatGPT/Claude-style
// checklist the agent maintains and revises as it works. It renders EXACTLY the current-plan projection
// (the owning module owns the plan; the dashboard authors nothing — P-SELF): an expandable card showing a
// "n/total done" header and each step with its status glyph. Collapsed by default so it stays quiet; the
// human expands to see the steps. Tokens/rem only (G-181). No library/internal identifier (G-197).

import { useState } from 'react';
import { Icon } from './Icon.tsx';
import type { IconName } from './Icon.tsx';
import type { ChatPlanView, PlanStepStatus } from '../surfaces/projections.ts';

/** The glyph + accessible word per step status (operational vocabulary only — no internals). */
const STATUS: Readonly<Record<PlanStepStatus, { readonly icon: IconName; readonly word: string }>> =
  {
    pending: { icon: 'chevron', word: 'to do' },
    in_progress: { icon: 'clock', word: 'in progress' },
    done: { icon: 'check', word: 'done' },
    blocked: { icon: 'ban', word: 'blocked' },
  };

/** One plan step row: a status glyph + the sanitised step text; done steps read struck-through. */
function PlanStepRow({
  text,
  status,
}: {
  readonly text: string;
  readonly status: PlanStepStatus;
}): React.JSX.Element {
  const mark = STATUS[status];
  return (
    <li className="cp-step" data-step-status={status}>
      <span className="cp-mark" aria-hidden="true">
        <Icon name={mark.icon} size={12} />
      </span>
      <span className="cp-text">{text}</span>
      <span className="cp-word">{mark.word}</span>
    </li>
  );
}

/** The inline plan card: an expandable checklist of the agent's current plan. Renders nothing when the
 *  agent has authored no plan (honest absence — never a fabricated empty plan). */
export function ChatPlan({
  plan,
}: {
  readonly plan: ChatPlanView | null;
}): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (plan === null || plan.steps.length === 0) return null;
  const done = plan.steps.filter((step) => step.status === 'done').length;
  const total = plan.steps.length;
  return (
    <div className="chat-plan" data-testid="chat-plan">
      <button
        type="button"
        className="cp-head"
        aria-expanded={open}
        aria-label={open ? 'Collapse the plan' : 'Expand the plan'}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="cp-ico" aria-hidden="true">
          <Icon name="tasks" size={13} />
        </span>
        <span className="cp-title">Plan</span>
        <span className="cp-count">
          {done}/{total} done
        </span>
        <span className={open ? 'cp-chev open' : 'cp-chev'} aria-hidden="true">
          <Icon name="chevdown" size={13} />
        </span>
      </button>
      {open ? (
        <ol className="cp-steps">
          {plan.steps.map((step, index) => (
            // Compose the id with its position: the module mints unique ids, but the key stays collision-proof
            // even if a projection ever carried a duplicate id (a duplicate React key silently drops a row).
            <PlanStepRow
              key={`${step.id}-${String(index)}`}
              text={step.text}
              status={step.status}
            />
          ))}
        </ol>
      ) : null}
    </div>
  );
}
